import { computed, nextTick, onBeforeUnmount, onMounted, ref, toValue, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import { useHostElement } from "#ce/composables/useHostElement";
import { RECONNECTED_EVENT } from "#ce/framework/events";
import { deepActiveElement } from "#ce/utils/focus";

const USE_TOP_LAYER_PROPERTY = "--sp-comp-popup-use-top-layer";

// jsdom lacks the Popover API and can't even parse the `:popover-open`
// pseudo-class (it throws), so guard the check — "can't tell" means closed.
function isPopoverOpen(el) {
  try {
    return el.matches?.(":popover-open") ?? false;
  } catch {
    return false;
  }
}

/**
 * Reveal the popup in the top layer via the native Popover API, with a
 * class-driven fallback for browsers that lack it or hosts that opt out via
 * `--sp-comp-popup-use-top-layer: false`. Pairs with `usePopupPlacement`
 * (Floating UI positions the element regardless of how it is shown).
 *
 * @param {object} options
 * @param {import("vue").Ref<HTMLElement|null>} options.popup Floating element ref.
 * @param {import("vue").Ref<boolean>} options.isActive Open state.
 * @param {() => boolean} options.enabled Floating (popover) variant — false for permanent.
 * @param {() => void} options.startAutoUpdate Begin Floating UI autoUpdate (run after the popup is shown).
 * @param {() => void} options.stopAutoUpdate Tear down Floating UI autoUpdate.
 * @param {() => boolean} [options.noRestoreFocus] The caller owns focus after closing.
 * @returns {{ popoverMode: import("vue").ComputedRef<string|undefined>, useFallbackOpen: import("vue").ComputedRef<boolean> }}
 */
export function usePopupVisibility({ popup, isActive, enabled, startAutoUpdate, stopAutoUpdate, noRestoreFocus }) {
  // Browsers below Chrome 114 (our supported 109/112) lack the Popover API:
  // showPopover() is undefined and `:popover-open` never matches, so the gate
  // `&:popover-open { display: block }` never fires and the popup stays hidden.
  // Feature-detect the method (computed so a runtime stub is honoured) and fall
  // back to a class-driven reveal there.
  const supportsPopover = computed(
    () => typeof window !== "undefined" && typeof window.HTMLElement?.prototype?.showPopover === "function",
  );

  // Hosts can opt out when their chrome must paint above popups.
  const useTopLayer = ref(true);

  function readTopLayerPreference(el) {
    return getComputedStyle(el).getPropertyValue(USE_TOP_LAYER_PROPERTY).trim().toLowerCase() !== "false";
  }

  onMounted(() => {
    if (popup.value) {
      useTopLayer.value = readTopLayerPreference(popup.value);
    }
  });

  // Only set the `popover` attribute where the API exists — an unsupported browser
  // would ignore it, and the `--open` fallback drives visibility there instead.
  const popoverMode = computed(() =>
    toValue(enabled) && supportsPopover.value && useTopLayer.value ? "manual" : undefined,
  );

  // Fallback reveal: floating popup, no Popover API or top layer opted out,
  // currently active.
  const useFallbackOpen = computed(
    () => toValue(enabled) && (!supportsPopover.value || !useTopLayer.value) && isActive.value,
  );

  async function showPopup() {
    await nextTick();
    // A rapid open→close during the tick can flip isActive back to false before
    // this resolves — bail so we neither show nor start autoUpdate on a popup that
    // should now be hidden.
    if (!isActive.value) {
      return;
    }
    const el = popup.value;
    if (!el || !toValue(enabled) || !el.isConnected) {
      return;
    }

    // Re-read each show (the inherited token can change between opens); a flip
    // re-renders the `popover` attribute, so settle a tick before showing.
    const nextUseTopLayer = readTopLayerPreference(el);
    if (useTopLayer.value !== nextUseTopLayer) {
      useTopLayer.value = nextUseTopLayer;
      await nextTick();
    }

    if (!isActive.value) {
      return;
    }

    // Without the Popover API — or top layer opted out — the `--open` class
    // handles visibility; only run Floating UI positioning here.
    if (supportsPopover.value && useTopLayer.value && !isPopoverOpen(el)) {
      try {
        el.showPopover();
      } catch {
        // Already open or not upgraded yet — ignore.
      }
    }
    // Start autoUpdate now that the element is shown/connected; starting earlier
    // would measure a `display: none` element.
    startAutoUpdate?.();
  }

  function hidePopup() {
    stopAutoUpdate?.();
    const el = popup.value;
    if (!el || !toValue(enabled)) {
      return;
    }
    if (supportsPopover.value && isPopoverOpen(el)) {
      if (toValue(noRestoreFocus)) {
        releaseFocus(el);
      }
      try {
        el.hidePopover();
      } catch {
        // noop
      }
    }
    // Clear the inline width the size() middleware writes (!important) so a stale
    // width can't override CSS if the popup later renders in a different mode or
    // against a different anchor. size() re-applies on the next positioning pass.
    el.style.removeProperty("width");
    el.style.removeProperty("min-width");
  }

  // Runs immediately so a popup mounted with `active` already true is revealed —
  // display is gated on `:popover-open` / `--open`, so the initial showPopup()
  // is what makes it visible. showPopup()'s `await nextTick()` defers the work
  // until the element is connected, so running during setup is safe.
  watch(
    isActive,
    (value) => {
      if (value) {
        showPopup();
      } else {
        hidePopup();
      }
    },
    { immediate: true },
  );

  // A popover leaves the top layer whenever its element leaves the document, and the browser
  // does not put it back when the element returns (a results refresh re-inserts the filter bar
  // around an open menu), so an active popup shows itself again on reconnection.
  function reassertPopup() {
    const el = popup.value;

    if (!isActive.value || !el?.isConnected || !toValue(enabled) || !supportsPopover.value || !useTopLayer.value) {
      return;
    }
    if (!isPopoverOpen(el)) {
      el.showPopover();
    }
  }

  onBeforeUnmount(hidePopup);

  useEventListener(useHostElement(), RECONNECTED_EVENT, reassertPopup);

  return { popoverMode, useFallbackOpen };
}

function releaseFocus(popup) {
  const active = deepActiveElement();
  let node = active;
  while (node && node !== popup) {
    node = node.assignedSlot ?? node.parentNode ?? node.host;
  }
  if (node === popup) {
    active?.blur?.();
  }
}
